/* eslint-disable generator-star-spacing */
/* eslint space-before-function-paren: "off" */
import { isEmpty } from 'lodash'
import { toast } from 'react-toastify'
import type { SagaIterator } from 'redux-saga'
import { call, put, takeLatest } from 'redux-saga/effects'
import { UpdateRequest } from '~api/types'
import { Punchlist } from '../../api'
import { errorTextTryingTo } from '../../helpers'
import { contractorsActions, territoriesActions } from '../actions'
import { ContractorItem } from '../types'
import {
  ActivateContractorAction,
  contractorsTypes,
  CreateContractorAction, DeactivateContractorAction, DeleteContractorAction,
  DeleteContractorTerritoryAction,
  FetchContractorsAction,
  RequestPayload, SetContractorTerritoryAction, UpdateContractorAction
} from './types'

export function* fetchContractors({
  payload,
  callback,
}: FetchContractorsAction): SagaIterator {
  let success = false
  try {
    const page = payload?.page ?? 0
    const size = payload?.size ?? 1000
    const institutional = payload?.institutional
    const searchParams = payload?.searchParams
    const start = page * size
    const limit = size

    const contractors = yield call(Punchlist.contractors.getContractors, {
      start,
      limit,
      searchParams,
      institutional,
    })

    if (contractors) {
      yield put(contractorsActions.setAllContractors(contractors))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the contractors'))
    if (callback) yield call(callback, false)
  }
}

export function* fetchAllContractors({
  payload,
  callback,
}: FetchContractorsAction): SagaIterator {
  let success = false
  try {
    const institutional = payload?.institutional
    const searchParams = payload?.searchParams

    const contractors = yield call(Punchlist.contractors.getContractors, {
      searchParams,
      institutional,
      start: 0,
      limit: 1000,
    })

    if (contractors) {
      yield put(contractorsActions.setAllContractors(contractors))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the contractors'))
    if (callback) yield call(callback, false)
  }
}

export function* createContractor({
  payload,
  callback,
}: CreateContractorAction): SagaIterator {
  try {
    const request: RequestPayload = payload

    const contractor = yield call(
      Punchlist.contractors.createContractor,
      request.contractor
    )

    if (!isEmpty(contractor)) {
      const page = payload?.page ?? 0
      const size = payload?.size ?? 1000
      const institutional = payload?.institutional

      const start = page * size
      const limit = size

      const contractors = yield call(Punchlist.contractors.getContractors, {
        start,
        limit,
        institutional,
      })

      if (contractors) {
        yield put(contractorsActions.setContractor(contractor))
        yield put(contractorsActions.setContractors(contractors))
        if (callback) yield call(callback, true, contractor)
      }
    }
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('create the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* patchContractor({
  payload,
  callback,
}: UpdateContractorAction): SagaIterator {
  try {
    if (payload.updateRequest) {
      const request: UpdateRequest = payload.updateRequest
      const contractor = yield call(
        Punchlist.contractors.updateContractor,
        payload.contractor.id as string,
        request
      )

      if (!isEmpty(contractor)) {
        const page = payload?.page ?? 0
        const size = payload?.size ?? 1000

        const start = page * size
        const limit = size

        const contractors = yield call(Punchlist.contractors.getContractors, {
          start,
          limit,
        })
        if (contractors) {
          yield put(contractorsActions.setContractor(contractor))
          yield put(contractorsActions.setContractors({ ...contractors, allItems: contractors, }))
          if (callback) yield call(callback, true)
        }
      }
    } else {
      if (callback) yield call(callback, false)
    }
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('update the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* updateContractor({
  payload,
  callback,
}: UpdateContractorAction): SagaIterator {
  try {
    const request: UpdateRequest = []

    for (const attr in payload.contractor) {
      if (attr !== 'id') {
        request.push({
          op: 'add',
          path: '/' + attr,
          value: payload.contractor[attr as keyof ContractorItem],
        })
      }
    }

    const contractor = yield call(
      Punchlist.contractors.updateContractor,
      payload.contractor.id as string,
      request
    )

    if (!isEmpty(contractor)) {
      const page = payload?.page ?? 0
      const size = payload?.size ?? 1000
      const institutional = payload?.institutional

      const start = page * size
      const limit = size

      const contractors = yield call(Punchlist.contractors.getContractors, {
        start,
        limit,
        institutional,
      })
      if (contractors) {
        yield put(contractorsActions.setContractors(contractors))
        if (callback) yield call(callback, true)
      }
    }
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('update the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* deleteContractor({
  payload,
  callback,
}: DeleteContractorAction): SagaIterator {
  try {
    yield call(Punchlist.contractors.deleteContractor, payload.contractor.id as string)

    if (callback) yield call(callback, true)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('delete the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* activateContractor({
  payload,
  callback,
}: ActivateContractorAction): SagaIterator {
  try {
    yield call(Punchlist.contractors.activateContractor, payload)
    if (callback) yield call(callback, true)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('activate the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* deactivateContractor({
  payload,
  callback,
}: DeactivateContractorAction): SagaIterator {
  try {
    yield call(Punchlist.contractors.deactivateContractor, payload)
    if (callback) yield call(callback, true)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('deactivate the contractor'))
    if (callback) yield call(callback, false)
  }
}

export function* setContractorTerritory({
  payload,
  callback,
}: SetContractorTerritoryAction): SagaIterator {
  try {
    yield call(Punchlist.contractors.setContractorTerritory, payload)

    const territories = yield call(Punchlist.territories.getTerritories, {
      start: 0,
      limit: 1000,
    })
    if (territories) {
      yield put(territoriesActions.setTerritories(territories))
    }
    if (callback) yield call(callback, true)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('set the contractor territory'))
    if (callback) yield call(callback, false)
  }
}

export function* deleteContractorTerritory({
  payload,
  callback,
}: DeleteContractorTerritoryAction): SagaIterator {
  try {
    yield call(Punchlist.contractors.deleteContractorTerritory, payload)
    const territories = yield call(Punchlist.territories.getTerritories, {
      start: 0,
      limit: 1000,
    })
    if (territories) {
      yield put(territoriesActions.setTerritories(territories))
    }
    if (callback) yield call(callback, true)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('delete the contractor territory'))
    if (callback) yield call(callback, false)
  }
}

export default function* saga(): SagaIterator {
  yield takeLatest(contractorsTypes.FETCH_CONTRACTORS, fetchContractors)
  yield takeLatest(contractorsTypes.FETCH_ALL_CONTRACTORS, fetchAllContractors)
  yield takeLatest(contractorsTypes.CREATE_CONTRACTOR, createContractor)
  yield takeLatest(contractorsTypes.UPDATE_CONTRACTOR, updateContractor)
  yield takeLatest(contractorsTypes.DELETE_CONTRACTOR, deleteContractor)
  yield takeLatest(contractorsTypes.PATCH_CONTRACTOR, patchContractor)
  yield takeLatest(contractorsTypes.ACTIVATE_CONTRACTOR, activateContractor)
  yield takeLatest(contractorsTypes.DEACTIVATE_CONTRACTOR, deactivateContractor)
  yield takeLatest(contractorsTypes.SET_CONTRACTOR_TERRITORY, setContractorTerritory)
  yield takeLatest(contractorsTypes.DELETE_CONTRACTOR_TERRITORY, deleteContractorTerritory)
}
